import React, { useEffect, useState } from 'react';
import './LGABudgetList.css';
import { Link } from 'react-router-dom';
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";

const LGABudgetList = () => {
    const [budgets, setBudgets] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [lgaSearch, setLgaSearch] = useState("");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedLgas, setSelectedLgas] = useState([]);
    const [showLgas, setShowLgas] = useState(false);

    // 🔍 Filtered LGAs
    const filteredLgas = lgas.filter(l =>
        l.name.toLowerCase().includes(lgaSearch.toLowerCase())
    );

    // ================= FETCH LGAs =================
    useEffect(() => {
        const fetchLgas = async () => {
            try {
                const { data } = await privateApi.get('/api/lga/budgets/lgas');
                setLgas(data.data || []);
            } catch (err) {
                console.error("Failed to fetch LGAs");
            }
        };

        fetchLgas();
    }, []);

    // ================= FETCH BUDGETS =================
    useEffect(() => {
        const fetchBudgets = async () => {
            try {
                setLoading(true);

                let url = `/api/lga/budgets?level=lga`;

                if (filterStatus !== 'all') {
                    url += `&status=${filterStatus}`;
                }

                if (selectedLgas.length > 0) {
                    url += `&lgaIds=${selectedLgas.join(',')}`;
                }

                const { data } = await privateApi.get(url);
                setBudgets(data.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch budgets.');
            } finally {
                setLoading(false);
            }
        };

        fetchBudgets();
    }, [filterStatus, selectedLgas]);

    // ================= HANDLERS =================
    const handleLgaChange = (id) => {
        setSelectedLgas(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    // ✅ Select all (filtered only)
    const isAllFilteredSelected =
        filteredLgas.length > 0 &&
        filteredLgas.every(l => selectedLgas.includes(l._id));

    const handleSelectAll = () => {
        const ids = filteredLgas.map(l => l._id);

        setSelectedLgas(prev => {
            const set = new Set(prev);
            ids.forEach(id => set.add(id));
            return Array.from(set);
        });
    };

    // ❌ Clear only filtered
    const handleClearFiltered = () => {
        const ids = filteredLgas.map(l => l._id);

        setSelectedLgas(prev =>
            prev.filter(id => !ids.includes(id))
        );
    };

    return (
        <div className="lga-budget-list-container">
            <header className="lga-budget-list-header">
                <h2>LGA Budgets</h2>
            </header>

            <div className="lga-budget-list-filter-and-action-button">
                <div className="lga-budget-list-filters">

                    {/* ================= LGA FILTER ================= */}
                    <div className={`lga-checkboxes ${showLgas ? 'active' : ''}`}>
                        <div
                            className="lga-checkboxes-toggle"
                            onClick={() => setShowLgas(prev => !prev)}
                        >
                            {showLgas ? 'Hide LGAs' : 'Filter by LGA'}
                        </div>

                        <div className="lga-checkboxes-list">

                            {/* 🔍 Search */}
                            <input
                                type="text"
                                placeholder="Search LGA..."
                                value={lgaSearch}
                                onChange={(e) => setLgaSearch(e.target.value)}
                                className="lga-search-input"
                            />

                            {/* 🔥 Actions */}
                            <div className="lga-actions">
                                <label className="select-all">
                                    <input
                                        type="checkbox"
                                        checked={isAllFilteredSelected}
                                        onChange={(e) =>
                                            e.target.checked
                                                ? handleSelectAll()
                                                : handleClearFiltered()
                                        }
                                    />
                                    Select All
                                </label>

                                <button
                                    type="button"
                                    className="clear-btn"
                                    onClick={handleClearFiltered}
                                >
                                    Clear
                                </button>
                            </div>

                            {/* 📋 List */}
                            {filteredLgas.length === 0 ? (
                                <p className="no-lga-found">No LGA found</p>
                            ) : (
                                filteredLgas.map(lga => (
                                    <label key={lga._id}>
                                        <input
                                            type="checkbox"
                                            checked={selectedLgas.includes(lga._id)}
                                            onChange={() => handleLgaChange(lga._id)}
                                        />
                                        {lga.name}
                                    </label>
                                ))
                            )}
                        </div>
                    </div>

                    {/* ================= STATUS FILTER ================= */}
                    <label htmlFor="statusFilter">Filter by Status:</label>
                    <select
                        id="statusFilter"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="draft">Draft</option>
                        <option value="approved">Approved</option>
                        <option value="locked">Locked</option>
                    </select>

                </div>

                <Link to="/lga/budgets/create" className="btn-create-budget">
                    Create Budget
                </Link>
            </div>

            {/* ================= TABLE ================= */}
            <div className="lga-budget-list-table-wrapper">
                {loading ? (
                    <Spinner loadingInfo="Loading LGA Budgets..." />
                ) : error ? (
                    <p className="lga-budget-list-error">{error}</p>
                ) : budgets.length === 0 ? (
                    <p>No budgets found.</p>
                ) : (
                    <table className="lga-budget-list-table">
                        <thead>
                            <tr>
                                <th>LGA</th>
                                <th>Year</th>
                                <th>Type</th>
                                <th>Revision</th>
                                <th>Status</th>
                                <th>Remark</th>
                                <th>Created By</th>
                                <th>Approved By</th>
                                <th>Locked By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {budgets.map(budget => (
                                <tr key={budget._id}>
                                    <td>{budget.lgaId?.name || '-'}</td>
                                    <td>{budget.year}</td>
                                    <td>{budget.type}</td>
                                    <td>{budget.revisionNumber}</td>
                                    <td>{budget.status}</td>
                                    <td>{budget.remark || '-'}</td>
                                    <td>{budget.createdBy?.name || '-'}</td>
                                    <td>{budget.approvedBy?.name || '-'}</td>
                                    <td>{budget.lockedBy?.name || '-'}</td>

                                    <td>
                                        <Link
                                            to={`/lga/budget`}
                                            state={{ budgetId: budget._id }}
                                            className="btn-view"
                                        >
                                            View
                                        </Link>

                                        {budget.status === 'draft' && (
                                            <Link
                                                to={`/lga/budget/edit`}
                                                state={{ budgetId: budget._id }}
                                                className="btn-edit"
                                            >
                                                Edit
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default LGABudgetList;